import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import { sendTradeAcceptedNotification } from '../../lib/notifications';

// Chase character image
import ChaseWave from '../../assets/images/ChaseWave.png';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const InboxIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const StarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// Calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Format distance for display (5-mile minimum for privacy)
const formatDistance = (miles) => {
  if (miles === null) return null;
  // Apply 5-mile minimum floor for privacy
  if (miles < 5) return 'Less than 5 mi';
  if (miles < 10) return `${Math.round(miles)} mi`;
  return `${Math.round(miles)} mi`;
};

// Helper to get current user data from users table
const getCurrentUserData = async () => {
  const { data } = await supabase.auth.getSession();
  const authId = data?.session?.user?.id;
  if (!authId) return null;

  const { data: userData, error } = await supabase
    .from('users')
    .select('id, latitude, longitude')
    .eq('auth_id', authId)
    .single();

  if (error || !userData) {
    console.error('Error fetching user data from users table:', error);
    return null;
  }

  return userData;
};

// Fetch trade requests from Supabase
const fetchTradeRequests = async (userId, currentUserLocation) => {
  try {
    const { data, error } = await supabase
      .from('trade_requests')
      .select(`
        id,
        requester_id,
        card_owner_id,
        card_id,
        offered_binder_id,
        status,
        viewed_by_owner,
        created_at,
        cards (id, name, digital_image_url),
        requester:users!trade_requests_requester_id_fkey (id, username, latitude, longitude, trade_level),
        card_owner:users!trade_requests_card_owner_id_fkey (id, username, latitude, longitude, trade_level)
      `)
      .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
      .in('status', ['pending', 'accepted', 'declined', 'completed', 'cancelled'])
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching trade requests:', error);
      return [];
    }

    return data.map((request) => {
      const requesterName = request.requester?.username || 'Trader';
      const cardOwnerName = request.card_owner?.username || 'Owner';

      const isIncoming = request.card_owner_id === userId;
      const partner = isIncoming ? request.requester : request.card_owner;

      // Calculate distance from current user to partner (lat/long used internally, not exposed)
      let distance = null;
      if (currentUserLocation?.latitude && currentUserLocation?.longitude && partner?.latitude && partner?.longitude) {
        distance = calculateDistance(
          currentUserLocation.latitude,
          currentUserLocation.longitude,
          partner.latitude,
          partner.longitude
        );
      }

      return {
        id: request.id,
        type: isIncoming ? 'incoming' : 'outgoing',
        status: request.status,
        requesterName: requesterName,
        ownerName: cardOwnerName,
        ownerUsername: request.card_owner?.username || 'The card owner',
        cardId: request.cards?.id,
        cardName: request.cards?.name || (request.cards?.deleted_at ? 'Card Removed' : 'Unknown Card'),
        cardImage: request.cards?.digital_image_url || '',
        createdAt: new Date(request.created_at),
        isNew: isIncoming && !request.viewed_by_owner && request.status === 'pending',
        requesterId: request.requester_id,
        cardOwnerId: request.card_owner_id,
        partnerId: partner?.id,
        partnerTradeLevel: partner?.trade_level || 1,
        distance: distance,
      };
    });
  } catch (error) {
    console.error('Error in fetchTradeRequests:', error);
    return [];
  }
};

// Mark request as viewed
const markRequestAsViewed = async (requestId, userId) => {
  try {
    await supabase
      .from('trade_requests')
      .update({ viewed_by_owner: true })
      .eq('id', requestId)
      .eq('card_owner_id', userId);
  } catch (error) {
    console.error('Error marking request as viewed:', error);
  }
};

// Filter config
const FILTER_CONFIG = {
  incoming: {
    title: 'Incoming',
    emptyTitle: 'No incoming requests',
    emptySubtitle: 'When someone wants to trade with you, it\'ll appear here',
  },
  outgoing: {
    title: 'Outgoing',
    emptyTitle: 'No outgoing requests',
    emptySubtitle: 'Requests you\'ve sent to other traders',
  },
  accepted: {
    title: 'Accepted',
    emptyTitle: 'No accepted requests',
    emptySubtitle: 'Accepted trades ready for scheduling',
  },
  declined: {
    title: 'Declined',
    emptyTitle: 'No declined requests',
    emptySubtitle: 'Requests that weren\'t accepted',
  },
  history: {
    title: 'History',
    emptyTitle: 'No request history',
    emptySubtitle: 'Your past trade requests will appear here',
  },
};

// Request Card Component
// Shows WHO wants WHICH card(s) with distance and trade level
// Supports grouped requests from same user with fan view
const RequestCard = ({ request, onAccept, onDecline, onView, onUserClick, onCardClick }) => {
  const { theme, isDark } = useTheme();
  const isIncoming = request.type === 'incoming';
  const isPending = request.status === 'pending';
  const partnerName = isIncoming ? request.requesterName : request.ownerName;

  // Check if this is a grouped request (multiple cards)
  const cards = request.cards || [{ cardId: request.cardId, cardName: request.cardName, cardImage: request.cardImage }];
  const cardCount = cards.length;
  const hasMultiple = cardCount > 1;

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const getStatusBadge = () => {
    switch (request.status) {
      case 'accepted':
        return { text: 'Accepted', color: theme.success, bg: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)' };
      case 'declined':
        return { text: 'Declined', color: theme.error, bg: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)' };
      default:
        return null;
    }
  };

  const statusBadge = getStatusBadge();
  const distanceText = formatDistance(request.distance);

  // Render fanned cards (up to 3 visible)
  const renderCardFan = () => {
    const visibleCards = cards.slice(0, 3);

    return (
      <button
        style={styles.cardFanContainer}
        onClick={() => onCardClick(request)}
      >
        {visibleCards.map((card, index) => (
          <img
            key={card.cardId || index}
            src={card.cardImage}
            alt={card.cardName}
            style={{
              ...styles.cardImage,
              ...styles.fannedCard,
              zIndex: 3 - index,
              transform: `translateX(${index * 12}px) rotate(${index * 5}deg)`,
            }}
          />
        ))}
      </button>
    );
  };

  return (
    <div style={{
      ...styles.requestCard,
      backgroundColor: theme.bgCard,
      borderColor: theme.border,
    }}>
      {/* New Badge */}
      {request.isNew && (
        <div style={{ ...styles.newBadge, backgroundColor: theme.success }}>
          <span style={{ ...styles.newBadgeText, color: isDark ? '#000000' : '#ffffff' }}>NEW</span>
        </div>
      )}

      {/* Card Image(s) - Fan if multiple, single if one */}
      {hasMultiple ? (
        <div style={styles.cardWithLabelFan}>
          {renderCardFan()}
          {isIncoming && <span style={{ ...styles.mineLabel, color: theme.accent }}>My Card</span>}
        </div>
      ) : (
        <div style={styles.cardWithLabel}>
          <button
            style={styles.cardImageContainer}
            onClick={() => onCardClick(request)}
          >
            <img
              src={request.cardImage}
              alt={request.cardName}
              style={{ ...styles.cardImage, backgroundColor: theme.border }}
            />
          </button>
          {isIncoming && <span style={{ ...styles.mineLabel, color: theme.accent }}>My Card</span>}
        </div>
      )}

      {/* Request Info */}
      <div style={styles.requestInfo}>
        <div style={styles.requestHeader}>
          {/* Clickable Username */}
          <button
            style={{
              ...styles.partnerNameButton,
              color: theme.text,
              textDecorationColor: theme.textSecondary,
            }}
            onClick={() => onUserClick(request)}
          >
            {partnerName}
          </button>
          <span style={{ ...styles.timeText, color: theme.textSecondary }}>{formatTime(request.createdAt)}</span>
        </div>

        {/* Distance and Trade Level */}
        <div style={styles.userMeta}>
          {distanceText && (
            <span style={{ ...styles.metaItem, color: theme.textMuted }}>
              <MapPinIcon />
              <span>{distanceText}</span>
            </span>
          )}
          <span style={{ ...styles.metaItem, color: theme.textMuted }}>
            <StarIcon />
            <span>Lvl {request.partnerTradeLevel}</span>
          </span>
        </div>

        <span style={{ ...styles.tradeSummary, color: theme.textMuted }}>
          {isIncoming ? 'wants ' : 'has '}
          {hasMultiple ? (
            <span style={{ ...styles.cardNameHighlight, color: theme.accent }}>{cardCount} of your cards</span>
          ) : (
            <>
              {isIncoming ? 'your ' : 'the '}
              <span style={{ ...styles.cardNameHighlight, color: theme.accent }}>{request.cardName}</span>
            </>
          )}
        </span>

        {/* Status Badge */}
        {request.status === 'accepted' && (
          <div style={{ ...styles.statusBadge, backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)', borderColor: theme.success }}>
            <span style={{ ...styles.statusText, color: theme.success }}>
              {isIncoming ? 'You accepted' : 'They accepted'}
            </span>
          </div>
        )}
        {request.status === 'declined' && (
          <div style={{ ...styles.statusBadge, backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)', borderColor: theme.error }}>
            <span style={{ ...styles.statusText, color: theme.error }}>
              {isIncoming ? 'You declined' : 'They declined'}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={styles.requestActions}>
        {isPending && isIncoming ? (
          // Incoming pending: Accept/Decline buttons
          <>
            <button style={{
              ...styles.acceptButton,
              backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
              borderColor: theme.success,
              color: theme.success,
            }} onClick={() => onAccept(request)}>
              <CheckIcon />
            </button>
            <button style={{
              ...styles.declineButton,
              backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
              borderColor: theme.error,
              color: theme.error,
            }} onClick={() => onDecline(request)}>
              <XIcon />
            </button>
          </>
        ) : isPending && !isIncoming ? (
          // Outgoing pending: Show waiting indicator
          <span style={{ ...styles.waitingText, color: theme.textSecondary }}>Waiting...</span>
        ) : request.status === 'accepted' ? (
          // Accepted: Show chat button
          <button style={{
            ...styles.chatButton,
            backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
            borderColor: theme.success,
            color: theme.success,
          }} onClick={() => onView(request)}>
            <ChevronRightIcon />
          </button>
        ) : request.status === 'declined' ? (
          // Declined: No button needed
          null
        ) : (
          // Other statuses: Show chevron
          <button style={{
            ...styles.viewButton,
            backgroundColor: theme.border,
            color: theme.textMuted,
          }} onClick={() => onView(request)}>
            <ChevronRightIcon />
          </button>
        )}
      </div>
    </div>
  );
};

// Arrow icons for modal navigation
const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRightLargeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

// Full Screen Card Modal with navigation for multiple cards
const CardModal = ({ cards, currentIndex, onClose, onNext, onPrev, partnerName, partnerId, onViewCollection }) => {
  const { theme, isDark } = useTheme();
  const [showTip, setShowTip] = useState(false);

  if (!cards || cards.length === 0) return null;

  const card = cards[currentIndex];
  const hasMultiple = cards.length > 1;

  const handleViewClick = () => {
    setShowTip(true);
  };

  const handleContinue = () => {
    onViewCollection();
  };

  const handleBack = () => {
    setShowTip(false);
  };

  // Tip screen with Chase character
  if (showTip) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={{
          ...styles.tipContent,
          backgroundColor: theme.bgCard,
          borderColor: theme.border,
        }} onClick={(e) => e.stopPropagation()}>
          <button style={{ ...styles.modalCloseButton, color: theme.text }} onClick={onClose}>
            <CloseIcon />
          </button>

          <div style={styles.tipMessageBox}>
            <p style={{ ...styles.tipMessage, color: theme.textMuted }}>
              If you want any of <span style={{ ...styles.tipHighlight, color: theme.accent }}>{partnerName}'s</span> cards,
              you <span style={{ ...styles.tipDont, color: theme.error }}>DON'T</span> need to send a separate trade request.
            </p>

            <p style={{ ...styles.tipMessage, color: theme.textMuted }}>
              Simply come back and <span style={{ ...styles.tipHighlight, color: theme.accent }}>accept this trade</span> to
              begin scheduling your meetup!
            </p>
          </div>

          <div style={styles.chaseRow}>
            <img
              src={ChaseWave}
              alt="Chase"
              style={styles.chaseImage}
            />
            <span style={{ ...styles.chaseIntro, color: theme.text }}>Hello, I'm Chase!</span>
          </div>

          <div style={styles.tipButtons}>
            <button style={{
              ...styles.tipBackButton,
              borderColor: theme.border,
              color: theme.textMuted,
            }} onClick={handleBack}>
              Back
            </button>
            <button style={styles.tipContinueButton} onClick={handleContinue}>
              View Collection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button style={{ ...styles.modalCloseButton, color: theme.text }} onClick={onClose}>
          <CloseIcon />
        </button>

        {/* Header text */}
        <p style={{ ...styles.modalHeaderText, color: theme.textMuted }}>
          {hasMultiple
            ? `These are your cards that ${partnerName} wants to trade`
            : `${partnerName} wants to trade for this card`
          }
        </p>

        <img
          src={card.cardImage}
          alt={card.cardName}
          style={styles.modalCardImage}
        />

        <div style={styles.modalCardInfo}>
          <h2 style={{ ...styles.modalCardName, color: theme.text }}>{card.cardName}</h2>
          <p style={{ ...styles.modalCardSubtitle, color: theme.textMuted }}>
            {hasMultiple ? `${currentIndex + 1} of ${cards.length}` : 'Your card'}
          </p>
        </div>

        {/* Navigation arrows - below the card */}
        {hasMultiple && (
          <div style={styles.modalNavRow}>
            <button
              style={{
                ...styles.modalNavButton,
                opacity: currentIndex > 0 ? 1 : 0.3,
                backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                color: theme.text,
              }}
              onClick={currentIndex > 0 ? onPrev : undefined}
              disabled={currentIndex === 0}
            >
              <ChevronLeftIcon />
            </button>
            <button
              style={{
                ...styles.modalNavButton,
                opacity: currentIndex < cards.length - 1 ? 1 : 0.3,
                backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                color: theme.text,
              }}
              onClick={currentIndex < cards.length - 1 ? onNext : undefined}
              disabled={currentIndex === cards.length - 1}
            >
              <ChevronRightLargeIcon />
            </button>
          </div>
        )}

        {/* View Collection Button */}
        <button style={{
          ...styles.modalViewCollectionButton,
          backgroundColor: theme.accent,
          color: isDark ? '#000000' : '#ffffff',
        }} onClick={handleViewClick}>
          View {partnerName}'s Collection
        </button>
      </div>
    </div>
  );
};

// Confirm Decline Modal
const ConfirmModal = ({ request, onConfirm, onCancel }) => {
  const { theme } = useTheme();
  if (!request) return null;

  const partnerName = request.type === 'incoming' ? request.requesterName : request.ownerName;

  return (
    <div style={styles.confirmOverlay} onClick={onCancel}>
      <div style={{
        ...styles.confirmContent,
        backgroundColor: theme.bgCard,
        borderColor: theme.border,
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ ...styles.confirmTitle, color: theme.text }}>Decline Request?</h3>
        <p style={{ ...styles.confirmText, color: theme.textMuted }}>
          Are you sure you want to decline the trade request from <span style={{ ...styles.confirmHighlight, color: theme.text }}>{partnerName}</span> for your <span style={{ ...styles.confirmHighlight, color: theme.text }}>{request.cardName}</span>?
        </p>
        <div style={styles.confirmActions}>
          <button style={{
            ...styles.confirmCancelButton,
            backgroundColor: theme.border,
            color: theme.text,
          }} onClick={onCancel}>
            Cancel
          </button>
          <button style={{
            ...styles.confirmDeclineButton,
            backgroundColor: theme.error,
            color: '#ffffff',
          }} onClick={onConfirm}>
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};

// Standalone Tip Modal (shown when tapping username)
const TipModal = ({ partner, onClose, onViewCollection }) => {
  const { theme, isDark } = useTheme();
  if (!partner) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{
        ...styles.tipContent,
        backgroundColor: theme.bgCard,
        borderColor: theme.border,
      }} onClick={(e) => e.stopPropagation()}>
        <button style={{ ...styles.modalCloseButton, color: theme.text }} onClick={onClose}>
          <CloseIcon />
        </button>

        <div style={styles.tipMessageBox}>
          <p style={{ ...styles.tipMessage, color: theme.textMuted }}>
            If you want any of <span style={{ ...styles.tipHighlight, color: theme.accent }}>{partner.name}'s</span> cards,
            you <span style={{ ...styles.tipDont, color: theme.error }}>DON'T</span> need to send a separate trade request.
          </p>

          <p style={{ ...styles.tipMessage, color: theme.textMuted }}>
            Simply come back and <span style={{ ...styles.tipHighlight, color: theme.accent }}>accept this trade</span> to
            begin scheduling your meetup!
          </p>
        </div>

        <div style={styles.chaseRow}>
          <img
            src={ChaseWave}
            alt="Chase"
            style={styles.chaseImage}
          />
          <span style={{ ...styles.chaseIntro, color: theme.text }}>Hello, I'm Chase!</span>
        </div>

        <div style={styles.tipButtons}>
          <button style={{
            ...styles.tipBackButton,
            borderColor: theme.border,
            color: theme.textMuted,
          }} onClick={onClose}>
            Back
          </button>
          <button style={styles.tipContinueButton} onClick={onViewCollection}>
            View Collection
          </button>
        </div>
      </div>
    </div>
  );
};

// Filter Tab Component
const FilterTab = ({ filter, isActive, count, onClick }) => {
  const { theme, isDark } = useTheme();
  return (
    <button
      style={{
        ...styles.filterTab,
        backgroundColor: isActive ? theme.accent : 'transparent',
        borderColor: isActive ? theme.accent : theme.border,
      }}
      onClick={onClick}
    >
      <span style={{
        ...styles.filterTabText,
        color: isActive ? (isDark ? '#000000' : '#ffffff') : theme.textMuted,
      }}>
        {FILTER_CONFIG[filter].title}
      </span>
      {count > 0 && (
        <span style={{
          ...styles.filterTabCount,
          backgroundColor: isActive ? (isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)') : theme.border,
          color: isActive ? (isDark ? '#000000' : '#ffffff') : theme.text,
        }}>
          {count}
        </span>
      )}
    </button>
  );
};

// Empty State Component
const EmptyState = ({ filter }) => {
  const { theme } = useTheme();
  const config = FILTER_CONFIG[filter];

  return (
    <div style={styles.emptyState}>
      <div style={{ color: theme.textSecondary }}>
        <InboxIcon />
      </div>
      <span style={{ ...styles.emptyTitle, color: theme.text }}>{config.emptyTitle}</span>
      <span style={{ ...styles.emptySubtitle, color: theme.textMuted }}>{config.emptySubtitle}</span>
    </div>
  );
};

// Main Requests Component
const Requests = () => {
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeProfile } = useProfile();

  const initialFilter = searchParams.get('filter') || 'incoming';
  const initialSearch = searchParams.get('search') || '';
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserLocation, setCurrentUserLocation] = useState(null);
  const [selectedCards, setSelectedCards] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [declineRequest, setDeclineRequest] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [tipPartner, setTipPartner] = useState(null); // For showing tip directly when tapping name

  // Group requests by partner for the same status
  const groupRequestsByPartner = (requests) => {
    const grouped = {};

    requests.forEach((request) => {
      const partnerId = request.type === 'incoming' ? request.requesterId : request.cardOwnerId;
      const key = `${partnerId}-${request.status}`;

      if (!grouped[key]) {
        grouped[key] = {
          ...request,
          cards: [],
          requestIds: [],
        };
      }

      grouped[key].cards.push({
        cardId: request.cardId,
        cardName: request.cardName,
        cardImage: request.cardImage,
        requestId: request.id,
      });
      grouped[key].requestIds.push(request.id);

      // Use the most recent createdAt and check for any new
      if (request.createdAt > grouped[key].createdAt) {
        grouped[key].createdAt = request.createdAt;
      }
      if (request.isNew) {
        grouped[key].isNew = true;
      }
    });

    return Object.values(grouped);
  };

  const loadRequests = async () => {
    // Use activeProfile (kid's data when parent views as kid)
    if (!activeProfile) {
      console.warn('No user data found; cannot fetch requests');
      setRequests([]);
      setLoading(false);
      return;
    }
    setCurrentUserId(activeProfile.id);
    setCurrentUserLocation({ latitude: activeProfile.latitude, longitude: activeProfile.longitude });
    const fetchedRequests = await fetchTradeRequests(activeProfile.id, activeProfile);
    setRequests(fetchedRequests);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    loadRequests();

    // Subscribe to real-time changes
    const subscription = supabase
      .channel('public:trade_requests')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trade_requests',
        },
        () => {
          loadRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user, activeProfile?.id]);

  // Filter requests based on active filter, search query, and group by partner
  const getFilteredRequests = () => {
    let filtered;
    switch (activeFilter) {
      case 'incoming':
        filtered = requests.filter(r => r.type === 'incoming' && r.status === 'pending');
        break;
      case 'outgoing':
        filtered = requests.filter(r => r.type === 'outgoing' && r.status === 'pending');
        break;
      case 'accepted':
        filtered = requests.filter(r => r.status === 'accepted');
        break;
      case 'declined':
        filtered = requests.filter(r => r.status === 'declined');
        break;
      case 'history':
        filtered = requests.filter(r => r.status !== 'pending');
        break;
      default:
        filtered = requests;
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(r => {
        // Check user names
        if (r.requesterName?.toLowerCase().includes(query)) return true;
        if (r.ownerName?.toLowerCase().includes(query)) return true;

        // Check all cards in the grouped fan (not just single cardName)
        const cards = r.cards || [];
        const matchesAnyCard = cards.some(card =>
          card.cardName?.toLowerCase().includes(query)
        );
        if (matchesAnyCard) return true;

        // Check date (format: "Dec 1" style from createdAt)
        if (r.createdAt) {
          const dateStr = r.createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toLowerCase();
          if (dateStr.includes(query)) return true;
        }

        return false;
      });
    }

    // Group by partner
    return groupRequestsByPartner(filtered);
  };

  const filteredRequests = getFilteredRequests();

  // Count per filter (grouped by partner - same as display)
  const counts = {
    incoming: groupRequestsByPartner(requests.filter(r => r.type === 'incoming' && r.status === 'pending')).length,
    outgoing: groupRequestsByPartner(requests.filter(r => r.type === 'outgoing' && r.status === 'pending')).length,
    accepted: groupRequestsByPartner(requests.filter(r => r.status === 'accepted')).length,
    declined: groupRequestsByPartner(requests.filter(r => r.status === 'declined')).length,
    history: groupRequestsByPartner(requests.filter(r => r.status !== 'pending')).length,
  };

  const handleBack = () => {
    navigate('/chat');
  };

  const handleAccept = async (request) => {
    try {
      // Get all request IDs (grouped requests have requestIds array)
      const allRequestIds = request.requestIds || [request.id];
      // Use sorted-first ID as primary (consistent with Chat.js and Messages.js)
      const primaryRequestId = [...allRequestIds].sort()[0];
      const acceptedAt = new Date().toISOString();

      // Update ALL trade requests to accepted
      const { error: updateError } = await supabase
        .from('trade_requests')
        .update({
          status: 'accepted',
          accepted_at: acceptedAt,
        })
        .in('id', allRequestIds);

      if (updateError) {
        console.error('Failed to accept requests:', updateError.message);
        alert('Failed to accept request. Please try again.');
        return;
      }

      // Insert system "Chase" message for the PRIMARY request only
      const chaseMessage = {
        trade_request_id: primaryRequestId,
        sender_id: '550e8400-e29b-41d4-a716-446655440000', // System Chase ID
        receiver_id: currentUserId,
        message: "Hello! I am Chase - co-owner of CardChase. Let's figure out where you are going to meet up and trade! Please select up to 3 locations that you would like to meet at!",
        message_type: 'system',
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      await supabase
        .from('messages')
        .upsert(chaseMessage, { onConflict: 'trade_request_id,sender_id,message' });

      console.log(`Accepted ${allRequestIds.length} request(s) from ${request.requesterName}`);

      // Send push notification to requester
      try {
        console.log('[Requests] Sending accept notification with:', {
          requesterId: request.requesterId,
          ownerUsername: request.ownerUsername,
          cardName: request.cardName,
          primaryRequestId
        });
        sendTradeAcceptedNotification(
          request.requesterId,
          request.ownerUsername,
          request.cardName,
          primaryRequestId
        );
      } catch (notifErr) {
        console.log('Notification send failed:', notifErr);
      }

      // Refresh the list
      loadRequests();

      // Navigate to chat with primary request
      navigate(`/chat/${primaryRequestId}`);
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('Failed to accept request. Please try again.');
    }
  };

  const handleDecline = (request) => {
    // Show confirmation modal
    setDeclineRequest(request);
  };

  const handleConfirmDecline = async () => {
    if (!declineRequest) return;

    try {
      const { error: updateError } = await supabase
        .from('trade_requests')
        .update({
          status: 'declined',
          accepted_at: null,
        })
        .eq('id', declineRequest.id);

      if (updateError) {
        console.error('Failed to decline request:', updateError.message);
        return;
      }

      console.log(`Declined request from ${declineRequest.requesterName} for ${declineRequest.cardName}`);

      // Close modal and refresh
      setDeclineRequest(null);
      loadRequests();
    } catch (error) {
      console.error('Error declining request:', error);
    }
  };

  const handleCancelDecline = () => {
    setDeclineRequest(null);
  };

  const handleView = (request) => {
    // Accepted trades go to chat, others go to trade detail
    if (request.status === 'accepted') {
      navigate(`/chat/${request.id}`);
    } else {
      navigate(`/trade/${request.id}`);
    }
  };

  const handleUserClick = async (request) => {
    // Mark as viewed if it's a new incoming request
    if (request.isNew && request.type === 'incoming') {
      await markRequestAsViewed(request.id, currentUserId);
      loadRequests(); // Refresh to update the NEW badge
    }
    // Show tip modal with partner info
    const isIncoming = request.type === 'incoming';
    const partnerName = isIncoming ? request.requesterName : request.ownerName;
    setTipPartner({
      name: partnerName,
      id: request.partnerId,
    });
  };

  const handleCardClick = (request) => {
    // Get the cards array from grouped request
    const cards = request.cards || [{ cardId: request.cardId, cardName: request.cardName, cardImage: request.cardImage }];
    const isIncoming = request.type === 'incoming';
    const partnerName = isIncoming ? request.requesterName : request.ownerName;

    setSelectedCards(cards);
    setSelectedCardIndex(0);
    setSelectedPartner({
      name: partnerName,
      id: request.partnerId,
    });
  };

  const handleCloseModal = () => {
    setSelectedCards(null);
    setSelectedCardIndex(0);
    setSelectedPartner(null);
  };

  const handleViewCollectionFromModal = () => {
    if (selectedPartner?.id) {
      navigate(`/user/${selectedPartner.id}/collection`, { state: { from: '/requests' } });
    }
  };

  const handleNextCard = () => {
    if (selectedCards && selectedCardIndex < selectedCards.length - 1) {
      setSelectedCardIndex(selectedCardIndex + 1);
    }
  };

  const handlePrevCard = () => {
    if (selectedCards && selectedCardIndex > 0) {
      setSelectedCardIndex(selectedCardIndex - 1);
    }
  };

  return (
    <div style={{ ...styles.container, backgroundColor: theme.bg }}>
      {/* Header */}
      <div style={{
        ...styles.header,
        backgroundColor: theme.bg,
        borderBottomColor: theme.bgCard,
      }}>
        <button style={{ ...styles.backButton, color: theme.accent }} onClick={handleBack}>
          <ArrowLeftIcon />
        </button>
        <div style={styles.headerContent}>
          <h1 style={{ ...styles.headerTitle, color: theme.text }}>Trade Requests</h1>
          <span style={{ ...styles.headerSubtitle, color: theme.textMuted }}>
            {filteredRequests.length} {activeFilter} request{filteredRequests.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Search Bar */}
      <div style={styles.searchContainer}>
        <div style={{
          ...styles.searchInputWrapper,
          backgroundColor: theme.bgCard,
          borderColor: theme.border,
          color: theme.textSecondary,
        }}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search by name or card..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...styles.searchInput, color: theme.text }}
          />
          {searchQuery && (
            <button
              style={{ ...styles.searchClear, color: theme.textSecondary }}
              onClick={() => setSearchQuery('')}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={styles.filterTabs}>
        {['incoming', 'outgoing', 'accepted', 'declined'].map((filter) => (
          <FilterTab
            key={filter}
            filter={filter}
            isActive={activeFilter === filter}
            count={counts[filter]}
            onClick={() => setActiveFilter(filter)}
          />
        ))}
      </div>

      {/* Requests List */}
      <div style={styles.requestsList}>
        {loading ? (
          // Loading skeletons
          [...Array(3)].map((_, i) => (
            <div key={i} style={{
              ...styles.skeletonCard,
              backgroundColor: theme.bgCard,
            }}>
              <div style={{ ...styles.skeletonImage, backgroundColor: theme.border }} />
              <div style={styles.skeletonContent}>
                <div style={{ ...styles.skeletonLine, backgroundColor: theme.border }} />
                <div style={{ ...styles.skeletonLine, width: '60%', backgroundColor: theme.border }} />
                <div style={{ ...styles.skeletonLine, width: '80%', backgroundColor: theme.border }} />
              </div>
            </div>
          ))
        ) : filteredRequests.length > 0 ? (
          filteredRequests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              onAccept={handleAccept}
              onDecline={handleDecline}
              onView={handleView}
              onUserClick={handleUserClick}
              onCardClick={handleCardClick}
            />
          ))
        ) : (
          <EmptyState filter={activeFilter} />
        )}
      </div>

      {/* Card Modal */}
      {selectedCards && selectedPartner && (
        <CardModal
          cards={selectedCards}
          currentIndex={selectedCardIndex}
          onClose={handleCloseModal}
          onNext={handleNextCard}
          onPrev={handlePrevCard}
          partnerName={selectedPartner.name}
          partnerId={selectedPartner.id}
          onViewCollection={handleViewCollectionFromModal}
        />
      )}

      {/* Decline Confirmation Modal */}
      {declineRequest && (
        <ConfirmModal
          request={declineRequest}
          onConfirm={handleConfirmDecline}
          onCancel={handleCancelDecline}
        />
      )}

      {/* Tip Modal (shown when tapping username) */}
      {tipPartner && (
        <TipModal
          partner={tipPartner}
          onClose={() => setTipPartner(null)}
          onViewCollection={() => {
            navigate(`/user/${tipPartner.id}/collection`, { state: { from: '/requests' } });
            setTipPartner(null);
          }}
        />
      )}
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    gap: 12,
    borderBottom: '1px solid',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 12,
  },

  // Search
  searchContainer: {
    padding: '12px 20px',
    paddingBottom: 0,
  },
  searchInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 12,
    border: '1px solid',
  },
  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    fontSize: 14,
  },
  searchClear: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Filter Tabs
  filterTabs: {
    display: 'flex',
    gap: 8,
    padding: '12px 20px',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  filterTab: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 20,
    border: '1px solid',
    background: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  filterTabText: {
    fontSize: 12,
    fontWeight: 600,
  },
  filterTabCount: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 10,
  },

  // Requests List
  requestsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 20px 100px 20px',
  },
  requestCard: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
    marginBottom: 12,
  },
  newBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    borderRadius: 3,
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  newBadgeText: {
    fontSize: 8,
    fontWeight: 700,
    lineHeight: 1,
  },

  // Card Image
  cardWithLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    width: 80,
  },
  cardWithLabelFan: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    flexShrink: 0,
    width: 80,
    marginTop: 4,
  },
  cardImageContainer: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  mineLabel: {
    fontSize: 9,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  cardImage: {
    width: 56,
    height: 78,
    borderRadius: 4,
    objectFit: 'contain',
    transition: 'transform 0.15s ease',
  },
  // Card Fan styles
  cardFanContainer: {
    position: 'relative',
    width: 80,
    height: 78,
    flexShrink: 0,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  fannedCard: {
    position: 'absolute',
    top: 0,
    left: 0,
    transformOrigin: 'bottom left',
    boxShadow: '2px 2px 8px rgba(0,0,0,0.3)',
  },
  moreCardsIndicator: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    borderRadius: 10,
    padding: '2px 6px',
    zIndex: 10,
  },
  moreCardsText: {
    fontSize: 10,
    fontWeight: 700,
  },

  // Request Info
  requestInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginLeft: 8,
  },
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  partnerName: {
    fontSize: 15,
    fontWeight: 600,
  },
  partnerNameButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  timeText: {
    fontSize: 12,
  },
  userMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
  },
  tradeSummary: {
    fontSize: 13,
  },
  cardNameHighlight: {
    fontWeight: 500,
  },
  initiatorTag: {
    display: 'block',
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 4,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    padding: '4px 8px',
    borderRadius: 4,
    border: '1px solid',
    marginTop: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusText: {
    fontSize: 10,
    fontWeight: 600,
    textAlign: 'center',
  },

  // Actions
  requestActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
  },
  acceptButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    border: '1px solid',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  declineButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    border: '1px solid',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    border: '1px solid',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  acceptedButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '8px 12px',
    borderRadius: 12,
    border: '1px solid',
    cursor: 'pointer',
  },
  acceptedButtonText: {
    fontSize: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  waitingText: {
    fontSize: 11,
    fontStyle: 'italic',
  },

  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  emptySubtitle: {
    fontSize: 13,
    textAlign: 'center',
  },

  // Loading Skeletons
  skeletonCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  skeletonImage: {
    width: 60,
    height: 84,
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  skeletonLine: {
    height: 12,
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },

  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 400,
    width: '100%',
  },
  modalCloseButton: {
    position: 'absolute',
    top: -50,
    right: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
  },
  modalNavRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 30,
    marginTop: 12,
  },
  modalNavButton: {
    border: '1px solid',
    borderRadius: '50%',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  modalCardImage: {
    maxWidth: '100%',
    maxHeight: '45vh',
    borderRadius: 10,
    objectFit: 'contain',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
  },
  modalCardInfo: {
    marginTop: 12,
    textAlign: 'center',
  },
  modalCardName: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  modalCardSubtitle: {
    fontSize: 12,
    marginTop: 4,
  },
  modalHeaderText: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 10,
    maxWidth: 280,
  },
  modalViewCollectionButton: {
    marginTop: 12,
    padding: '10px 20px',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // Tip Screen Styles
  tipContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 24,
    padding: 28,
    maxWidth: 340,
    width: '100%',
    textAlign: 'center',
    border: '1px solid',
  },
  tipMessageBox: {
    marginBottom: 20,
  },
  tipMessage: {
    fontSize: 15,
    lineHeight: 1.7,
    margin: 0,
    marginBottom: 12,
  },
  tipHighlight: {
    fontWeight: 600,
  },
  tipDont: {
    fontWeight: 700,
  },
  chaseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  chaseImage: {
    width: 140,
    height: 140,
    objectFit: 'contain',
  },
  chaseIntro: {
    fontSize: 20,
    fontWeight: 700,
  },
  tipButtons: {
    display: 'flex',
    gap: 12,
    width: '100%',
  },
  tipBackButton: {
    flex: 1,
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: '1px solid',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tipContinueButton: {
    flex: 2,
    padding: '14px 20px',
    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    border: 'none',
    borderRadius: 12,
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(14, 165, 233, 0.3)',
  },

  // Confirm Modal Styles
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  confirmContent: {
    borderRadius: 16,
    border: '1px solid',
    padding: 24,
    maxWidth: 320,
    width: '100%',
  },
  confirmTitle: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
    marginBottom: 12,
    textAlign: 'center',
  },
  confirmText: {
    fontSize: 14,
    margin: 0,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  confirmHighlight: {
    fontWeight: 600,
  },
  confirmActions: {
    display: 'flex',
    gap: 12,
  },
  confirmCancelButton: {
    flex: 1,
    padding: '12px 16px',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmDeclineButton: {
    flex: 1,
    padding: '12px 16px',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default Requests;
